class PmProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    this.variants = this.parseJson('[data-product-variants]', []);
    this.context = this.parseJson('[data-product-context]', { currency: 'USD', locale: 'en' });
    this.variantInput = this.querySelector('[data-product-variant-id]');
    this.price = this.querySelector('[data-product-price]');
    this.submit = this.querySelector('[data-product-submit]');
    this.submitLabel = this.querySelector('[data-product-submit-label]');
    this.mediaList = this.querySelector('[data-product-media-list]');
    this.mediaItems = [...this.querySelectorAll('[data-product-media]')];
    this.mediaCurrent = this.querySelector('[data-product-media-current]');
    this.modal = this.querySelector('[data-product-modal]');

    this.querySelectorAll('[data-product-option-value]').forEach((input) => {
      input.addEventListener('change', () => this.updateVariant());
    });

    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
    this.querySelector('[data-product-media-previous]')?.addEventListener('click', () => this.moveMedia(-1));
    this.querySelector('[data-product-media-next]')?.addEventListener('click', () => this.moveMedia(1));
    this.mediaList?.addEventListener('scroll', () => this.queueMediaUpdate(), { passive: true });
    this.mediaItems.forEach((item) => item.addEventListener('click', () => this.openModal(item.dataset.mediaId)));
    this.querySelector('[data-product-open-modal]')?.addEventListener('click', () => this.openModal());
    this.querySelector('[data-product-close-modal]')?.addEventListener('click', () => this.modal?.close());
    this.modal?.addEventListener('click', (event) => {
      if (event.target === this.modal) this.modal.close();
    });
    this.querySelector('[data-product-copy-link]')?.addEventListener('click', () => this.copyShareLink());

    this.dataset.ready = 'true';
    this.updateMediaCounter();
  }

  parseJson(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || '');
    } catch (_) {
      return fallback;
    }
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-product-option-value]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
  }

  updateVariant() {
    const selected = this.selectedOptions();
    const variant = this.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]));

    if (!variant) {
      this.variantInput.value = '';
      this.submit.disabled = true;
      this.submitLabel.textContent = 'Unavailable';
      return;
    }

    this.variantInput.value = variant.id;
    this.submit.disabled = !variant.available;
    this.submitLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
    this.price.textContent = new Intl.NumberFormat(this.context.locale, {
      style: 'currency',
      currency: this.context.currency
    }).format(variant.price / 100);

    const mediaId = variant.featured_media?.id || variant.featured_image?.id;
    if (mediaId) this.selectMedia(String(mediaId));

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  changeQuantity(delta) {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;
    const minimum = Math.max(1, Number(input.min) || 1);
    input.value = Math.max(minimum, (Number(input.value) || minimum) + delta);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  currentMediaIndex() {
    if (!this.mediaList || this.mediaItems.length === 0) return 0;
    const width = this.mediaList.clientWidth || 1;
    return Math.max(0, Math.min(this.mediaItems.length - 1, Math.round(this.mediaList.scrollLeft / width)));
  }

  moveMedia(delta) {
    const index = Math.max(0, Math.min(this.mediaItems.length - 1, this.currentMediaIndex() + delta));
    this.mediaItems[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  selectMedia(mediaId) {
    const media = this.mediaItems.find((item) => item.dataset.mediaId === mediaId);
    if (!media) return;
    media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  queueMediaUpdate() {
    cancelAnimationFrame(this.mediaFrame);
    this.mediaFrame = requestAnimationFrame(() => this.updateMediaCounter());
  }

  updateMediaCounter() {
    if (this.mediaCurrent) this.mediaCurrent.textContent = String(this.currentMediaIndex() + 1);
  }

  openModal(mediaId) {
    if (!this.modal) return;
    this.modal.showModal();
    const target = mediaId ? this.modal.querySelector(`[data-modal-media-id="${CSS.escape(mediaId)}"]`) : null;
    target?.scrollIntoView({ block: 'start' });
  }

  async copyShareLink() {
    const input = this.querySelector('[data-product-share-url]');
    if (!input) return;
    try {
      await navigator.clipboard.writeText(input.value);
    } catch (_) {
      input.select();
      document.execCommand('copy');
    }
  }
}

if (!customElements.get('pm-product')) {
  customElements.define('pm-product', PmProduct);
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations?.innerHTML.trim()) this.innerHTML = recommendations.innerHTML;
      })
      .catch(() => {});
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
